import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { ModalConfirm, NavBar } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { useCreateExternalLinkMutation } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { ModalOrder } from "@src/components/widgets/Modals/ModalOrder";
import { can, withCan } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, useContext, useRef } from "react";
import { ModalCreateContract } from "./form/contract/ModalCreateContract";
import { ModalCreateOtherDocument } from "./form/otherDocument/ModalCreateOtherDocument";
import {
  DocumentsCtx,
  QuickFilters,
  type StoreDocuments,
} from "./StoreDocuments";
import { TableContracts } from "./table/contracts/TableContracts";
import {
  ExtLinkCreateForm,
  type TExtLinkCreateFormRef,
} from "./table/external-links/CreateForm";
import { TableExternalLinks } from "./table/external-links/TableExternalLinks";
import { TableOrders } from "./table/orders/TableOrders";
import { TableOtherDocuments } from "./table/other-documents/TableOtherDocuments";

interface Store {
  store: StoreDocuments;
  variant?: "tabs" | "menu";
}

export const TabTableNavigation = observer(function TabTableNavigation({
  store,
  variant = "tabs",
}: Store) {
  if (variant === "tabs") {
    return (
      <NavBar>
        {store.showParts.has("contracts") && (
          <NavBar.Item
            active={store.activeQuickFilter === QuickFilters.Contracts}
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.Contracts);
              store.resetTableState();
            }}
          >
            <Trans>Contracts</Trans>
          </NavBar.Item>
        )}
        {store.showParts.has("orders") && (
          <NavBar.Item
            active={store.activeQuickFilter === QuickFilters.Orders}
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.Orders);
              store.resetTableState();
            }}
          >
            <Trans>Orders</Trans>
          </NavBar.Item>
        )}
        {store.showParts.has("other") && (
          <NavBar.Item
            active={store.activeQuickFilter === QuickFilters.Other}
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.Other);
              store.resetTableState();
            }}
          >
            <Trans>Other</Trans>
          </NavBar.Item>
        )}
        {store.showParts.has("external_links") && (
          <NavBar.Item
            active={store.activeQuickFilter === QuickFilters.ExternalLinks}
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.ExternalLinks);
              store.resetTableState();
            }}
          >
            <Trans>External links</Trans>
          </NavBar.Item>
        )}
      </NavBar>
    );
  }

  const activeLabel =
    {
      [QuickFilters.Contracts]: t`Contracts`,
      [QuickFilters.Orders]: t`Orders`,
      [QuickFilters.Other]: t`Other`,
      [QuickFilters.ExternalLinks]: t`External links`,
    }[store.activeQuickFilter] ?? t`Contracts`;

  return (
    <Menu>
      <MenuButton
        as={Button}
        minW="fit-content"
        colorScheme="grey"
        variant="outline"
      >
        <Trans>Document type:</Trans>&nbsp;{activeLabel}
      </MenuButton>
      <MenuList>
        {store.showParts.has("contracts") && (
          <MenuItem
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.Contracts);
              store.resetTableState();
            }}
          >
            <Trans>Contracts</Trans>
          </MenuItem>
        )}
        {store.showParts.has("orders") && (
          <MenuItem
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.Orders);
              store.resetTableState();
            }}
          >
            <Trans>Orders</Trans>
          </MenuItem>
        )}
        {store.showParts.has("other") && (
          <MenuItem
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.Other);
              store.resetTableState();
            }}
          >
            <Trans>Other</Trans>
          </MenuItem>
        )}
        {store.showParts.has("external_links") && (
          <MenuItem
            onClick={() => {
              store.setActiveQuickFilter(QuickFilters.ExternalLinks);
              store.resetTableState();
            }}
          >
            <Trans>External links</Trans>
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
});

export const DocumentsTables: FunctionComponent<
  React.PropsWithChildren<Store>
> = ({ store }) => (
  <React.Fragment>
    {store.activeQuickFilter === QuickFilters.Contracts && <TableContracts />}
    {store.activeQuickFilter === QuickFilters.Orders && <TableOrders />}
    {store.activeQuickFilter === QuickFilters.ExternalLinks && (
      <TableExternalLinks />
    )}
    {store.activeQuickFilter === QuickFilters.Other && <TableOtherDocuments />}
  </React.Fragment>
);

export const AddDocumentBtn = withCan(
  { do: ["contract_create", "order_create", "otherDocument_create"] },
  observer(function AddDocumentBtn() {
    const store = useContext(DocumentsCtx);
    const extLinkFormRef = useRef<TExtLinkCreateFormRef>(null);

    const m1 = useDisclosure();
    const m2 = useDisclosure();
    const m3 = useDisclosure();
    const extLinkModal = useDisclosure();

    const [{ fetching: creatingExtLink }, createExtLink] =
      useCreateExternalLinkMutation();

    return (
      <Box pos="relative" zIndex={20}>
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<Icon name="chevron-down" />}
            variant="solid"
          >
            <Trans>Add new</Trans>
          </MenuButton>

          <MenuList>
            {can("contract_create") && store.showParts.has("contracts") && (
              <MenuItem onClick={m1.onOpen}>{t`Contract`}</MenuItem>
            )}

            {can("order_create") && store.showParts.has("orders") && (
              <MenuItem onClick={m2.onOpen}>{t`Order`}</MenuItem>
            )}

            {store.showParts.has("external_links") && (
              <MenuItem onClick={extLinkModal.onOpen}>
                <Trans>External link</Trans>
              </MenuItem>
            )}

            {can("otherDocument_create") && store.showParts.has("other") && (
              <MenuItem onClick={m3.onOpen}>{t`Other document`}</MenuItem>
            )}
          </MenuList>
        </Menu>

        <ModalCreateContract {...m1} />
        <ModalOrder
          prefillFields={store.prefillFormFields}
          onSubmit={store.refetchOrders}
          {...m2}
        />
        <ModalCreateOtherDocument {...m3} />
        <ModalConfirm
          {...extLinkModal}
          onConfirm={() => {
            extLinkFormRef.current?.onSubmit();
          }}
          closeOnConfirm={false}
          heading={t`Add new external link`}
          isLoading={creatingExtLink}
        >
          <ExtLinkCreateForm
            ref={extLinkFormRef}
            type={store.extLinkType}
            projectOptions={store.projects}
            brandOptions={store.brands}
            onSubmit={async (form) => {
              if (!form.model_id || !form.model_type) return;

              await createExtLink({
                input: form,
              });

              extLinkModal.onClose();
              store.refetchExtLinks?.();
              store.setActiveQuickFilter(QuickFilters.ExternalLinks);
              store.resetTableState();
            }}
          />
        </ModalConfirm>
      </Box>
    );
  }),
);
